import { useState } from "react";
import {
  Alert, Box, Button, Checkbox, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { otConsumptionsApi, type CreateOtConsumptionLineDto, type CreateOtImplantDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

/** UC-FN-HOS-02 — Create OT consumption with implants (UDI mandatory per row). */
export function CreateOtConsumptionPage() {
  const navigate = useNavigate();
  const [uhid, setUhid] = useState("");
  const [admissionId, setAdmissionId] = useState("");
  const [procedureCode, setProcedureCode] = useState("");
  const [otWarehouseId, setOtWarehouseId] = useState("");
  const [surgicalKitId, setSurgicalKitId] = useState("");
  const [surgeon, setSurgeon] = useState("");
  const [procedureAt, setProcedureAt] = useState("");
  const [lines, setLines] = useState<CreateOtConsumptionLineDto[]>([
    { itemId: "", batchId: "", qty: 1, isImplant: false },
  ]);
  const [implants, setImplants] = useState<CreateOtImplantDto[]>([]);

  const mutation = useMutation({
    meta: { successMessage: "OT consumption created" },
    mutationFn: () => otConsumptionsApi.create({
      uhid, admissionId: admissionId || undefined, procedureCode, otWarehouseId,
      surgicalKitId: surgicalKitId || undefined, surgeon: surgeon || undefined,
      procedureAt: procedureAt || undefined,
      lines: lines.filter(l => l.itemId && l.batchId && l.qty > 0),
      implants: implants.filter(i => i.itemId && i.batchId && i.udi),
    }),
    onSuccess: () => navigate("/hospital/ot"),
  });

  const updateLine = (i: number, patch: Partial<CreateOtConsumptionLineDto>) =>
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const updateImplant = (i: number, patch: Partial<CreateOtImplantDto>) =>
    setImplants(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New OT consumption</Typography>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField label="UHID" value={uhid} required size="small" sx={{ flex: 1 }}
              onChange={(e) => setUhid(e.target.value)} />
            <TextField label="Admission ID" value={admissionId} size="small" sx={{ flex: 1 }}
              onChange={(e) => setAdmissionId(e.target.value)} />
            <TextField label="Procedure code" value={procedureCode} required size="small" sx={{ flex: 1 }}
              onChange={(e) => setProcedureCode(e.target.value)} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="OT warehouse ID" value={otWarehouseId} required size="small" sx={{ flex: 1 }}
              onChange={(e) => setOtWarehouseId(e.target.value)} />
            <TextField label="Surgical kit ID (optional BOM)" value={surgicalKitId} size="small" sx={{ flex: 1 }}
              onChange={(e) => setSurgicalKitId(e.target.value)} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Surgeon" value={surgeon} size="small" sx={{ flex: 1 }}
              onChange={(e) => setSurgeon(e.target.value)} />
            <TextField label="Procedure at" type="datetime-local" value={procedureAt} size="small"
              InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
              onChange={(e) => setProcedureAt(e.target.value)} />
          </Stack>

          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>Items consumed</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item ID</TableCell><TableCell>Batch ID</TableCell>
                <TableCell width={100}>Qty</TableCell><TableCell width={90}>Implant?</TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={i}>
                  <TableCell><TextField size="small" fullWidth value={l.itemId}
                    onChange={(e) => updateLine(i, { itemId: e.target.value })} /></TableCell>
                  <TableCell><TextField size="small" fullWidth value={l.batchId}
                    onChange={(e) => updateLine(i, { batchId: e.target.value })} /></TableCell>
                  <TableCell><TextField size="small" type="number" value={l.qty}
                    onChange={(e) => updateLine(i, { qty: Number(e.target.value) })} /></TableCell>
                  <TableCell><Checkbox checked={l.isImplant}
                    onChange={(e) => updateLine(i, { isImplant: e.target.checked })} /></TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() =>
                      setLines(prev => prev.filter((_, idx) => idx !== i))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setLines(prev => [...prev, { itemId: "", batchId: "", qty: 1, isImplant: false }])}>
            Add item
          </Button>

          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>Implant register (UDI)</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item ID</TableCell><TableCell>Batch ID</TableCell>
                <TableCell>UDI</TableCell><TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {implants.map((u, i) => (
                <TableRow key={i}>
                  <TableCell><TextField size="small" fullWidth value={u.itemId}
                    onChange={(e) => updateImplant(i, { itemId: e.target.value })} /></TableCell>
                  <TableCell><TextField size="small" fullWidth value={u.batchId}
                    onChange={(e) => updateImplant(i, { batchId: e.target.value })} /></TableCell>
                  <TableCell><TextField size="small" fullWidth value={u.udi}
                    onChange={(e) => updateImplant(i, { udi: e.target.value })} /></TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() =>
                      setImplants(prev => prev.filter((_, idx) => idx !== i))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setImplants(prev => [...prev, { itemId: "", batchId: "", udi: "" }])}>
            Add implant
          </Button>

          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          {(!uhid || !procedureCode || !otWarehouseId || !lines.some(l => l.itemId && l.batchId && l.qty > 0)) && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {!uhid ? "UHID is required."
                : !procedureCode ? "Procedure code is required."
                : !otWarehouseId ? "Pick an OT warehouse."
                : "Add at least one line with an item, batch and quantity."}
            </Alert>
          )}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => navigate("/hospital/ot")}>Cancel</Button>
            <Button variant="contained" onClick={() => mutation.mutate()}
              disabled={!uhid || !procedureCode || !otWarehouseId ||
                !lines.some(l => l.itemId && l.batchId && l.qty > 0) || mutation.isPending}>
              Save draft
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
